import React from "react";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  Badge,
  Breadcrumbs,
  ButtonGroup,
  Card,
  CopyableItem,
  DisabledText,
  ErrorState,
  FileTextIcon,
  Grid,
  InfoGroup,
  InfoRow,
  Inline,
  ListIcon,
  LoadingState,
  Meter,
  PageHeader,
  Section,
  Stack,
  SubHeadline,
  cssColorFromBasedOnPercentage,
} from "@KristianIV/common-ui";
import PageLayout from "../../shell/PageLayout";
import RouteButton from "../../shell/RouteButton";
import { ApplicationState } from "../../reducers";
import { tutToDate } from "../../util";
import { buildDeviceRows } from "../deviceRows";
import { NoiseMeter, deviceEventsPath, deviceLogsPath } from "../DevicesTable";
import FastTrack from "../FastTrack";
import HostRows from "./HostRows";
import WallieCard from "./WallieCard";
import { parseWallieStats } from "../wallie";
import FastTrackBadge from "../FastTrackBadge";
import { HostHealthBadges } from "../HostWarningBadge";
import { resolveOfflineThresholds, scoutStateColor } from "../scoutState";
import { useOrganisationListeners } from "../useOrganisationListeners";
import styles from "../OrganisationPage.module.css";
import DeviceAnalytics from "./DeviceAnalytics";
import DeviceConfiguration from "./DeviceConfiguration";

const DevicePage = ({ match }: RouteComponentProps<{ id: string; deviceId: string }>) => {
  const { id: organisationId, deviceId } = match.params;
  const history = useHistory();

  const jwtToken = useSelector((state: ApplicationState) => state.login.jwtToken);
  const organisationInfo = useSelector((state: ApplicationState) => state.organisation.organisationInfo);
  const scouts = useSelector((state: ApplicationState) => state.organisation.scouts);
  const observations = useSelector((state: ApplicationState) => state.organisation.observations);
  const fastTrackScouts = useSelector((state: ApplicationState) => state.organisation.fastTrackScouts);

  useOrganisationListeners(organisationId, jwtToken);

  const scout = Object.values(scouts).find((s) => s.status.deviceId === deviceId || s.deviceId === deviceId);
  // A single device is always shown, so keep it at Offline rather than ignored for up to a week.
  const thresholds = resolveOfflineThresholds(organisationInfo?.offline, true);
  const row = scout ? buildDeviceRows([scout], observations, fastTrackScouts, thresholds)[0] : undefined;
  const scoutsLoaded = Object.keys(scouts).length > 0;

  return (
    <PageLayout>
      <Breadcrumbs
        items={[
          {
            label: organisationInfo ? organisationInfo.name : "Organisation",
            onClick: () => history.push(`/organisation/${organisationId}`),
          },
        ]}
        onHomeClick={() => history.push("/")}
        currentLabel={row ? row.name : deviceId}
      />

      {!organisationInfo || !row || !scout ? (
        scoutsLoaded && organisationInfo ? (
          <ErrorState message="Device not found in this organisation" />
        ) : (
          <LoadingState message="Loading device..." />
        )
      ) : (
        <>
          <PageHeader
            title={row.name}
            actions={
              <ButtonGroup aria-label="Device pages">
                <RouteButton to={deviceEventsPath(organisationId, row.deviceId)} icon={<ListIcon />}>
                  Events
                </RouteButton>
                <RouteButton to={deviceLogsPath(organisationId, row.deviceId)} icon={<FileTextIcon />}>
                  Logs
                </RouteButton>
              </ButtonGroup>
            }
          >
            <Badge size="big" color={scoutStateColor(row.state)}>
              {row.stateLabel}
            </Badge>
            {row.overridden && (
              <Badge size="big" color="blue">
                Overridden
              </Badge>
            )}
            {row.fastTrack && <FastTrackBadge fastTrack={row.fastTrack} deviceId={row.deviceId} size="big" />}
          </PageHeader>

          <Section>
              <Grid>
                <Card interactive={false} padded>
                  <SubHeadline>General</SubHeadline>
                  <InfoGroup>
                    <InfoRow label="Device ID">
                      <CopyableItem value={row.deviceId} label="device ID" />
                    </InfoRow>
                    <InfoRow label="Last update">{tutToDate(row.lastUpdate).toLocaleString()}</InfoRow>
                    <InfoRow label="Battery">
                      <Meter
                        value={row.batteryPercent}
                        valueLabel={`${row.batteryPercent}%${row.charging ? " · charging" : ""}`}
                        color={cssColorFromBasedOnPercentage(row.batteryPercent)}
                        className={styles.meter}
                      />
                    </InfoRow>
                    <InfoRow label="Noise level">
                      {row.noiseDbfs === undefined ? (
                        <DisabledText>No audio statistics reported</DisabledText>
                      ) : (
                        <NoiseMeter dbfs={row.noiseDbfs} size="normal" />
                      )}
                    </InfoRow>
                    <InfoRow label="Events / min">{row.eventsPerMinute}</InfoRow>
                  </InfoGroup>
                </Card>
                <Card interactive={false} padded>
                  <Stack gap="lg">
                    <Inline gap="md">
                      <SubHeadline noMargin>Device</SubHeadline>
                      {scout.status.host && <HostHealthBadges host={scout.status.host} />}
                    </Inline>
                    <InfoGroup>
                      <InfoRow label="Manufacturer">{scout.status.deviceInfo.manufacturer}</InfoRow>
                      <InfoRow label="Model">{scout.status.deviceInfo.model}</InfoRow>
                      <InfoRow label="Android SDK">{scout.status.deviceInfo.sdk}</InfoRow>
                      <InfoRow label="App version">{scout.status.appVersion}</InfoRow>
                      {scout.status.host && <HostRows host={scout.status.host} />}
                    </InfoGroup>
                  </Stack>
                </Card>
                {scout.status.titan && <WallieCard titan={scout.status.titan} stats={parseWallieStats(scout.status.timeService?.details)} />}
              </Grid>
          </Section>

          <Section>
              <DeviceConfiguration scout={scout} />
          </Section>

          <Section>
              <Grid>
                <DeviceAnalytics deviceId={row.deviceId} />
                <FastTrack scout={scout} state={row.state} />
              </Grid>
          </Section>
        </>
      )}
    </PageLayout>
  );
};

export default DevicePage;
